import React, { useState, FormEvent, useEffect } from "react";
import {
  Link,
  useNavigate,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import { auth, db } from "../../firebase/config";
import { useAuth } from "../../utils/AuthContext";
import Config from "../../Config";
import { useSendEmail } from "../../hooks/useSendEmail";
import { verificationCodeEmail } from "../../emails";

import { Eye, EyeOff } from "lucide-react";

import PrimaryButton from "../../components/Buttons/PrimaryButton";
import PasswordStrengthMeter, {
  getPasswordScore,
} from "../../components/PasswordStrengthMeter";

const Signup: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [name, setName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [apiError, setApiError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { sendEmail } = useSendEmail();

  useEffect(() => {
    const state = location.state as { name?: string; email?: string } | null;
    if (state?.name) setName(state.name);
    if (state?.email) setEmail(state.email);
  }, [location.state]);

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate("/home/dashboard", { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    const checkEmailVerification = async () => {
      const user = auth.currentUser;
      if (user && !authLoading) {
        await user.reload();
        if (!user.emailVerified) {
          sessionStorage.setItem("pendingVerificationEmail", user.email || "");
          navigate("/verify-code", { replace: true });
        }
      }
    };
    checkEmailVerification();
  }, [navigate, authLoading]);

  const handleSignup = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setApiError("");
    setLoading(true);

    if (!name.trim()) {
      setApiError("Please enter your name.");
      setLoading(false);
      return;
    }
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(email)) {
      setApiError("Please enter a valid email address.");
      setLoading(false);
      return;
    }
    if (password !== confirmPassword) {
      setApiError("Passwords do not match.");
      setLoading(false);
      return;
    }
    if (getPasswordScore(password) < 3) {
      setApiError("Please choose a stronger password.");
      setLoading(false);
      return;
    }

    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
      );
      const user = userCredential.user;

      // Mirror the profile to Firestore in the background — this must not block
      // (or fail) the signup flow, which proceeds via the backend below.
      setDoc(doc(db, "users", user.uid), {
        name: name.trim(),
        email: email,
        createdAt: new Date().toISOString(),
      }).catch((error) => {
        console.error("Error writing user profile to Firestore:", error);
      });

      const codeRes = await fetch(
        `${Config.API_URL}/api/auth/send-verification-code`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, firebase_uid: user.uid }),
        },
      );
      const codeData = await codeRes.json().catch(() => ({}));
      // The backend must return a verification code; without it the user can
      // never complete verification, so fail loudly rather than emailing a
      // missing code and navigating on to /verify-code.
      if (!codeRes.ok || !codeData?.code) {
        throw new Error(
          codeData?.detail ||
            "Could not send a verification code. Please try again.",
        );
      }
      // Fire-and-forget: the verification email is written to Firestore via the
      // mail collection and must not block (or fail) navigation to /verify-code.
      sendEmail({
        to: email,
        template: verificationCodeEmail({ code: codeData.code }),
      }).catch((error) => {
        console.error("Error sending verification email:", error);
      });

      sessionStorage.setItem("pendingVerificationEmail", email);
      sessionStorage.setItem("pendingVerificationName", name.trim());

      if (searchParams.get("plan") === "individual") {
        localStorage.setItem("pendingPlan", "individual");
      }

      navigate("/verify-code");
    } catch (error: any) {
      console.error("Signup error:", error);

      if (error.code === "auth/email-already-in-use") {
        setApiError("This email is already registered. Please login instead.");
      } else {
        setApiError(error.message || "Signup failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex justify-center items-center bg-white">
      <div className="w-full max-w-sm px-4">
        <h2 className="text-xl font-semibold tracking-tight text-gray-900 mb-1">
          Create an account
        </h2>
        <p className="text-sm text-gray-500 mb-6">
          Get started for free. No credit card required.
        </p>

        <form onSubmit={handleSignup} className="space-y-4">
          <div>
            <label
              htmlFor="signup-name"
              className="block text-sm font-medium text-gray-700 mb-1.5"
            >
              Name
            </label>
            <input
              id="signup-name"
              type="text"
              className="w-full h-10 px-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1d365d] focus:border-[#1d365d] transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={loading}
            />
          </div>

          <div>
            <label
              htmlFor="signup-email"
              className="block text-sm font-medium text-gray-700 mb-1.5"
            >
              Email
            </label>
            <input
              id="signup-email"
              type="email"
              className="w-full h-10 px-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1d365d] focus:border-[#1d365d] transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
            />
          </div>

          <div>
            <label
              htmlFor="signup-password"
              className="block text-sm font-medium text-gray-700 mb-1.5"
            >
              Password
            </label>
            <div className="relative">
              <input
                id="signup-password"
                type={showPassword ? "text" : "password"}
                className="w-full h-10 px-3 pr-10 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1d365d] focus:border-[#1d365d] transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 transition-colors"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
            <PasswordStrengthMeter password={password} minScore={3} />
          </div>

          <div>
            <label
              htmlFor="signup-confirm-password"
              className="block text-sm font-medium text-gray-700 mb-1.5"
            >
              Confirm password
            </label>
            <input
              id="signup-confirm-password"
              type="password"
              className="w-full h-10 px-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1d365d] focus:border-[#1d365d] transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={loading}
            />
          </div>

          <div className="flex items-start gap-2">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 accent-[#1d365d] border-gray-300 rounded disabled:cursor-not-allowed"
              id="termsCheck"
              required
              disabled={loading}
            />
            <label className="text-sm text-gray-500" htmlFor="termsCheck">
              By creating an account, you agree to our{" "}
              <Link
                to="/legal/terms-of-service"
                className="text-[#1d365d] hover:text-[#2d4a75] transition-colors"
              >
                Terms & Conditions
              </Link>{" "}
              and{" "}
              <Link
                to="/legal/privacy-policy"
                className="text-[#1d365d] hover:text-[#2d4a75] transition-colors"
              >
                Privacy Policy
              </Link>
              .
            </label>
          </div>

          {apiError && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {apiError}
            </p>
          )}

          <PrimaryButton className="w-full" type="submit" disabled={loading}>
            {loading ? "Creating account..." : "Sign up"}
          </PrimaryButton>
        </form>

        <div className="border-t border-gray-100 mt-6 pt-6 text-center">
          <p className="text-sm text-gray-500">
            Already have an account?{" "}
            <button
              onClick={() => navigate("/login")}
              className="text-[#1d365d] font-medium hover:text-[#2d4a75] transition-colors"
            >
              Sign in
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Signup;
